<template>
  <v-card
    id="dates-card"
    style="height:500px;"
    :disabled="disabled"
  >
    <v-card-title>
      Dates
    </v-card-title>
    <CalendarHeader
      @setToday="setToday"
      @prev="prev"
      @next="next"
      @changeType="(t) => { type = t }"
    />

    <v-sheet
      height="400"
      id="calendar-container">
      <v-calendar
        v-model="focus"
        id="calendar"
        ref="calendar"
        color="orange"
        :events="events"
        event-color="orange"
        :type="type"
        @click:event="handleEventClick"
        @click:date="viewDay"
        @change="getMonthBounds"
        @click.native="handleClick"
        @click:day="handleDateClick"
        @click:time="handleDateClick"
      >
        <template v-slot:event="{ eventParsed }">
          <strong class="event-label-time">
          {{ dateTimeToLocaleTime(eventParsed.start.date + 'T' + eventParsed.start.time) }}
          &mdash;
          {{ dateTimeToLocaleTime(eventParsed.end.date + 'T' + eventParsed.end.time) }}
          </strong>
          <span class="event-label-name">
            {{ eventParsed.input.name }}
          </span>
        </template>
      </v-calendar>
    </v-sheet>

    <v-menu
      v-model="addEventMenu"
      :close-on-content-click="false"
      absolute
      :position-x="addEventMenuX"
      :position-y="addEventMenuY"
      :transition="null"
      top
    >
      <TimePickerCard
        @addTime="handleAddEvent"
        @cancel="addEventMenu = false"
      />
    </v-menu>

    <v-menu
      v-model="updateEventMenu"
      :close-on-content-click="false"
      :activator="selectedElement"
      top
    >
      <EventMenu
        :color="selectedEvent.color"
        :startDateTime="selectedEvent.start"
        :endDateTime="selectedEvent.end"
        @remove="handleRemoveEvent"
        @close="updateEventMenu = false"
        @updateTime="handleUpdateEvent"
      />
    </v-menu>
  </v-card>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import CalendarHeader from './CalendarHeader'
import EventMenu from './EventMenu'
import TimePickerCard from './TimePickerCard'

export default {
  name: 'DatesCard',

  components: {
    CalendarHeader,
    EventMenu,
    TimePickerCard
  },

  props: ['disabled'],

  data: () => ({
    addEventMenu: false,
    currentDateTimePicked: {},
    currentDatePicked: '',
    clickTarget: null,
    focus: '',
    type: 'month',
    dateClickedElement: null,
    selectedEvent: {},
    selectedElement: null,
    updateEventMenu: false,
    events: [],
    addEventMenuX: 0,
    addEventMenuY: 0
  }),

  computed: {
    ...mapState({
      currentlyViewedChallenge: state => state.challenge.currentlyViewedChallenge
    })
  },

  mounted () {
    this.$refs.calendar.checkChange()
  },

  methods: {
    ...mapMutations(['setCurrentlyViewedChallenge']),

    // ////////////////////// //
    // click response methods //
    // ////////////////////// //

    handleClick (e) {
      // changes the position of the event menu as handleDateClick
      // does not have access to client coordinates
      this.addEventMenuX = e.clientX
      this.addEventMenuY = e.clientY
      e.stopPropagation()
    },

    handleDateClick (date) {
      if (!this.addEventMenu) {
        this.addEventMenu = true
        this.currentDatePicked = date.date
      } else {
        // must turn off old addEventMenu or else
        // there's a flicker to the new location before
        // the old event menu turns off
        this.addEventMenu = false
      }
    },

    handleEventClick ({ nativeEvent, event }) {
      // this method handles clicks on events
      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        this.currentDatePicked = event.start.toISOString().substr(0, 10)
        setTimeout(() => { this.updateEventMenu = true }, 10)
      }

      if (this.updateEventMenu) {
        this.updateEventMenu = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },

    // ////////////////////// //
    // Event changing methods //
    // ////////////////////// //

    handleAddEvent (times) {
      // add the date in ISO time with the time stored in locale time
      // since the time is stored as "9:00am", must be converted to milliseconds
      // and added to the date
      const startTime = new Date(+new Date(
        `${this.currentDatePicked}T00:00:00`) + this.localeTimeToMilliseconds(times.startTime)
      ).toISOString()
      const endTime = new Date(+new Date(
        `${this.currentDatePicked}T00:00:00`) + this.localeTimeToMilliseconds(times.endTime)
      ).toISOString()

      const updatedChallenge = this.currentlyViewedChallenge
      updatedChallenge.schedule.push(
        {
          startTime: startTime,
          endTime: endTime
        }
      )

      // // update the date in store with the corresponding time
      this.setCurrentlyViewedChallenge(updatedChallenge)

      this.addEventMenu = false
      this.getEventsForMonth()
    },

    handleUpdateEvent (times) {
      // updating an event time is done by removing the last clicked
      // event, and adding a new one
      this.handleRemoveEvent()
      this.handleAddEvent(times)
    },

    handleRemoveEvent () {
      const start = this.selectedEvent.start
      const end = this.selectedEvent.end
      const dates = this.currentlyViewedChallenge.schedule.map(d => ({
        start: d.startTime,
        end: d.endTime
      }))
      const index = dates.findIndex(
        d => d.start === start.toISOString() && d.end === end.toISOString()
      )

      if (index > -1) {
        const updatedChallenge = this.currentlyViewedChallenge
        updatedChallenge.schedule.splice(index, 1)
      }

      this.getEventsForMonth()
      this.updateEventMenu = false
      this.selectedEvent = {}
      this.selectedElement = null
    },

    // //////////////// //
    // Calendar methods //
    // //////////////// //

    viewDay ({ date }) {
      this.focus = date
      this.type = 'day'
    },

    setToday () {
      this.focus = ''
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },

    getMonthBounds ({ start, end }) {
      this.firstDayOfMonth = new Date(`${start.date}T00:00:00`)
      this.lastDayOfMonth = new Date(`${end.date}T23:59:59`)

      this.getEventsForMonth()
    },

    getEventsForMonth () {
      // only get the events that are in the currently shown month
      const dates = this.currentlyViewedChallenge.schedule.map(d => ({
        start: new Date(d.startTime),
        end: new Date(d.endTime),
        timed: true
      }))
      this.events = dates.filter(
        e => this.firstDayOfMonth < e.start < this.lastDayOfMonth)
    },

    // ////////////// //
    // Helper methods //
    // ////////////// //

    dateTimeToLocaleTime (dateTime) {
      // converts a datetime like '2020-07-25T14:30:00' to a localetime
      // like '9:00am'
      const timeString = (new Date(dateTime).toLocaleTimeString()).split(':')
      const hour = timeString[0]
      const minute = timeString[1]
      const meridiem = timeString[timeString.length - 1].split(' ')[1]
      return hour + ':' + minute + ' ' + meridiem
    },

    localeTimeToMilliseconds (time) {
      // converts a locale time like '9:00am' to milliseconds from the same day's 12:00am
      let [hours, minutes] = time.substr(0, time.length - 2).split(':').map(Number)
      // all hours after noon should have 12 added, to convert to 24 hour time
      if (time.includes('pm') && hours !== 12) hours += 12
      // 12:00am should be converted to 0
      if (time.includes('am') && hours === 12) hours -= 12
      return 1000 * 60 * (hours * 60 + minutes)
    }
  }
}
</script>

<style lang="sass" scoped>
  #dates-card
    padding: 2px
    width: 100%

  #date-picker
    background-image: linear-gradient(to right, rgb(255, 149, 24), rgb(255, 100, 100)) !important

  .event-label-time
    margin: 4px

</style>
